// Components==============
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "@tanstack/react-router";
import useGetCategories from "actions/quiz/useGetCategories";
import Button from "components/UI/Button/Button";
import useJoyStickNavigation, {
  joystickEvent,
} from "hooks/UseJoyStickNavigation";
import useNavigationAudio from "hooks/useNavigationAudio";
import { useState, useContext } from "react";
import QuizLayout from "../Quiz/components/QuizLayout/QuizLayout";
import styles from "./PickSubjects.module.scss";
import SubjectCard from "./SubjectCard/SubjectCard";
import useStoreTop3Themes from "actions/user/useStoreTop3Themes";
import { UserInfoContext } from "context/UserInfoContext";
// =========================

export default function PickSubjects() {
  const [currentPosition, setCurrentPosition] = useState<number | null>(1);

  const navigate = useNavigate();

  const [endQuizSelected, setEndQuizSelected] = useState(false);
  const [eventCounter, setEventCounter] = useState(0);
  const { checkmark, select, menu, confirmNumber } = useNavigationAudio();
  const [endQuizModal, setEndQuizModal] = useState(false);
  const [nextSelected, setNextSelected] = useState(false);
  const [pickedAnswers, setPickedAnswers] = useState<number[]>([]);

  const { userInfo } = useContext(UserInfoContext);

  const { data: categoriesData } = useGetCategories();
  const totalItems = categoriesData?.length || 0;

  const {mutateAsync: storeTop3Themes} = useStoreTop3Themes();

  // We pass data as an argument instead of reading from context to avoid stale reads
  const handleComplete = async () => {
    checkmark.play();
    console.log("end");

    storeTop3Themes({
      id: userInfo?.id, // Replace with actual user ID from context or props
      top3_themes: pickedAnswers.join(","),
    }).then(() => {
      navigate({ to: "/end" });
    }).catch((error) => {
      console.error("Error storing top 3 themes:", error);
      // Handle error (e.g., show a notification to the user)
    });
  };

  const handleEnter = () => {
    // if on next button handle complete
    if (nextSelected) {
      handleComplete();
      return;
    }
    // if on quiz end button show modal
    if (endQuizSelected) {
      setEndQuizModal(true);
      return;
    }

    // return when there is no current position (shouldn't happen)
    if (currentPosition === null || currentPosition < 1) return;

    // return when there is no category data (shouldn't happen)
    const currentItem = categoriesData?.[currentPosition - 1];
    if (!currentItem) return;

    let newAnswers: number[];

    // Check if item is already picked
    if (pickedAnswers.includes(currentItem.id)) {
      // Remove it from the array
      newAnswers = pickedAnswers.filter((id) => id !== currentItem.id);
      confirmNumber.play();
    } else {
      // Add it to the array (only if less than 3)
      if (pickedAnswers.length >= 3) {
        menu.play();
        return;
      }
      newAnswers = [...pickedAnswers, currentItem.id];
      confirmNumber.play();
    }

    setPickedAnswers(newAnswers);

    // // Check if we have exactly 3 answers then auto navigate to next button
    // if (newAnswers.length === 3) {
    //   setNextSelected(true);
    //   setCurrentPosition(null);
    // }
  };

  const handleNavigateBack = () => {};

  const navigateLeft = () => {
    // when in the top or bottom button don't do anything
    if (nextSelected || endQuizSelected) return;

    // when there is no current position (shouldn't happen) return
    if (currentPosition === null) return;

    let nextPosition = currentPosition;

    // Wrap to end of row if we are at the beginning
    if (currentPosition % 4 === 1) {
      nextPosition = currentPosition + 3;
      if (nextPosition > totalItems) return;
    } else {
      nextPosition = currentPosition - 1;
    }

    setCurrentPosition(nextPosition);
    select.play();
  };

  const navigateRight = () => {
    // when in the top or bottom button don't do anything
    if (nextSelected || endQuizSelected) return;

    // when there is no current position (shouldn't happen) return
    if (currentPosition === null) return;

    let nextPosition = currentPosition;

    // Wrap to beginning of row if we are at the end
    if (currentPosition % 4 === 0) {
      nextPosition = currentPosition - 3;
      if (nextPosition > totalItems) return;
    } else {
      // navigate to next position if it exists
      nextPosition = currentPosition + 1;
      if (nextPosition > totalItems) return;
    }

    setCurrentPosition(nextPosition);
    select.play();
  };

  const navigateDown = () => {
    // when on quiz end button go back to the first subject
    if (endQuizSelected) {
      setEndQuizSelected(false);
      setCurrentPosition(1);
      select.play();
      return;
    }

    // when there is no current position (shouldn't happen) return
    if (currentPosition === null) return;

    // calculate next position
    const nextPosition = currentPosition + 4;

    // only navigate to next button if we have picked 3 answers and are in the last row
    if (nextPosition > totalItems && pickedAnswers.length === 3) {
      setCurrentPosition(null);
      setNextSelected(true);
      select.play();
      return;
    }
    // when there is no next position to go to return
    if (nextPosition > totalItems) return;

    // go to the next position
    setCurrentPosition(nextPosition);
    select.play();
  };

  const navigateUp = () => {
    // when in next button go to the last subject
    if (nextSelected) {
      setNextSelected(false);
      setCurrentPosition(categoriesData?.length || 0);
      select.play();
      return;
    }

    // when there is no current position (shouldn't happen) return
    if (currentPosition === null) return;

    // Go to the top button if we are in the first row
    const nextPosition = currentPosition - 4;
    if (nextPosition < 1) {
      setCurrentPosition(null);
      setEndQuizSelected(true);
      menu.play();
      return;
    }

    // go to the next position if it exists
    setCurrentPosition(nextPosition);
    select.play();
  };

  const handleJoyStick = (event: joystickEvent) => {
    setEventCounter((prev) => prev + 1);

    switch (event) {
      case "left":
        navigateLeft();
        break;

      case "right":
        navigateRight();
        break;

      case "down":
        navigateDown();
        break;

      case "up":
        navigateUp();
        break;

      case "enter":
        handleEnter();
        break;

      default:
        break;
    }
  };

  console.log(pickedAnswers, "pickedAnswers");

  useJoyStickNavigation(handleJoyStick);

  return (
    <QuizLayout
      title={"Wat zijn de drie belangrijkste onderwerpen voor jou?"}
      endQuizSelected={endQuizSelected}
      eventCounter={eventCounter}
      endQuizModal={endQuizModal}
      setEndQuizModal={setEndQuizModal}
      // This is set to 0 to show the backButton as inactive
      questionIndex={0}
    >
      <>
        <div className={styles.subjects}>
          {categoriesData?.map((category, index) => (
            <SubjectCard
              selected={currentPosition === index + 1}
              active={pickedAnswers.includes(category.id)}
              inactive={
                pickedAnswers.length === 3 &&
                !pickedAnswers.includes(category.id)
              }
              key={category.id}
              category={category}
            />
          ))}
        </div>
        <Button
          inactive={pickedAnswers.length !== 3}
          variant={nextSelected ? "secondary" : "primary"}
        >
          Doorgaan <FontAwesomeIcon icon={faCheck} />
        </Button>
      </>
    </QuizLayout>
  );
}
